/**
 * v3 route table — fd-yevz.3. The single source of truth mapping every
 * screen in design-system/design_handoff_v3/README.md's § State `screen`
 * enum to a real route path and its nav/topbar section name.
 *
 * `V3ScreenId` is transcribed from the prototype's own literal state values
 * (design_handoff_v3/Planning Redesign v3.dc.html, `renderVals()`: the
 * `subs` nav-group table and the `go(...)`/`openItem`/`openPlanScreen`
 * calls around lines 2208-2253) rather than from the README's compressed
 * `prod.*` / `deploy.*` glob shorthand — the literal values are what
 * `-v3-route-map.test.ts` derives its coverage check from, independently of
 * this table, so an omission here is still caught.
 *
 * `plan.plans`/`plan`/`item` keep their pre-existing `/plans/...` paths
 * (preserved per scope, not renamed) since real, working, tested screens
 * already live there. Every other screen is new.
 */

export type V3ScreenId =
  | "home"
  | "prod.overview"
  | "prod.memories"
  | "memory.notes"
  | "memory.documents"
  | "memory"
  | "prod.opportunities"
  | "prod.designSystem"
  | "prod.health"
  | "plan.plans"
  | "plan.tasks"
  | "plan"
  | "task"
  | "layouts"
  | "build"
  | "build.tomerge"
  | "build.merged"
  | "item"
  | "item.build"
  | "item.task"
  | "deploy.status"
  | "deploy.releases"
  | "deploy.env"
  | "integrations"
  | "integrations.repos"
  | "integrations.repo"
  | "integrations.services"
  | "settings.project"
  | "members"
  | "org.projects"
  | "org.data"
  | "usage"
  | "org";

/**
 * Feature detail's back link and lit sidebar row (handoff § State:
 * `itemFrom`) — literal values match the prototype exactly (`itemFrom ===
 * "plan" | "merged" | "tomerge"`, default `"build"`); see
 * ImplementationPage.tsx's `featureBackLink`. "task" (Kris, Sep 18, 2026) is
 * not the prototype's.
 *
 * Since Kris, Sep 28, 2026 (issue #1177, option A) the build page lives only
 * under Build, so this names the page that LISTS the feature, never the page
 * it was opened from: Implement, Ready to merge or Merged by the feature's
 * state, and "plan" or "task" only for a feature off Build, which only its
 * plan's or task's own page lists (buildDoorPage, issue #1197 G8.16). "task"
 * is also the task's old address while it looks up the feature to forward to
 * (`/tasks/<taskId>/build`).
 */
export type ItemFrom = "plan" | "build" | "tomerge" | "merged" | "task";

export interface V3ScreenEntry {
  /** Nav sub-item / section label, verbatim from the handoff's § Navigation table. */
  section: string;
  /** Route path. Parameterized segments use TanStack's `$param` syntax. */
  path: string;
}

function pathSegments(path: string): string[] {
  return path.split("/").filter(Boolean);
}

/**
 * Matches `pathname` against one `V3_SCREENS` path template. `null` means no
 * match; otherwise reports whether every static segment matched literally
 * ("exact") or a `$param` segment had to stand in for at least one of them
 * ("param") — `screenForPath` uses this to prefer literal matches: a literal
 * path would otherwise tie with a `$param` path of the same segment count
 * (`/plans/archive` against `/plans/$planId` did, until that page went).
 */
function matchScreenPath(pathname: string, screenPath: string): "exact" | "param" | null {
  const pathSegs = pathSegments(pathname);
  const screenSegs = pathSegments(screenPath);
  if (pathSegs.length !== screenSegs.length) return null;
  let usedParam = false;
  for (let i = 0; i < screenSegs.length; i++) {
    if (screenSegs[i].startsWith("$")) {
      usedParam = true;
      continue;
    }
    if (screenSegs[i] !== pathSegs[i]) return null;
  }
  return usedParam ? "param" : "exact";
}

/**
 * Looks up the `V3_SCREENS` entry whose `path` template matches `pathname`
 * (`$param` segments match any single path segment) — the route table
 * "reading its own section name" for a consumer like Topbar. Returns
 * `undefined` for a pathname with no matching screen (e.g. `/`, `/account`,
 * or admin routes — none of those are part of the v3 screen enum).
 */
export function screenForPath(pathname: string): V3ScreenEntry | undefined {
  let paramMatch: V3ScreenEntry | undefined;
  for (const entry of Object.values(V3_SCREENS)) {
    const kind = matchScreenPath(pathname, entry.path);
    if (kind === "exact") return entry;
    if (kind === "param" && !paramMatch) paramMatch = entry;
  }
  return paramMatch;
}

export interface ComingSoonCopy {
  /** Breadcrumb-style label passed to `ComingSoon`'s `title`, e.g. "Product → Overview". */
  title: string;
  /**
   * What the area will do, grounded in design_handoff_v3/README.md's §
   * Screens description for it. Never a date (handoff's scope decision #1:
   * "say what the area will do, not a date").
   */
  description: string;
}

/**
 * The ten backend-less v3 screens (handoff § Scope decision #1: whole areas
 * with no backend get their nav entry plus ComingSoon, never a fabricated
 * screen) and the copy their route renders. This is the single source those
 * route files read `title`/`description` from — see
 * `-backend-less-screens.test.tsx`, which renders every route named here and
 * fails if its content doesn't match, catching a screen added here without
 * its route file being wired to match.
 *
 * `integrations` isn't here (fd-x0ml.1): its Repositories half now reads
 * real data, so the screen no longer renders a whole-page ComingSoon — see
 * integrations/index.tsx. `integrations.services` IS here (ex-qbfo90): once
 * the screen split in two, the Services half was left with no backend of its
 * own, which is exactly this list's contract — a whole area with no backend
 * gets its nav entry plus ComingSoon. Its description is the same sentence
 * the pre-split Services card carried, so the copy did not change when it
 * moved; `integrations.repos`/`integrations.repo` stay out, since both read
 * real repo data.
 */
export const BACKEND_LESS_SCREENS: Partial<Record<V3ScreenId, ComingSoonCopy>> = {
  "prod.opportunities": {
    title: "Product → Opportunities",
    description:
      "Will surface what Forged would look at next, grounded in something it read or observed, with a button to plan it.",
  },
  "deploy.status": {
    title: "Deploy → Status",
    description: "Will show whether the current build is ready to promote, plus the health of each environment.",
  },
  "deploy.releases": {
    title: "Deploy → Releases",
    description: "Will list each release and the features and tasks it carries, so a release is readable without a diff.",
  },
  "deploy.env": {
    title: "Deploy → Environments",
    description: "Will show how each environment is built, its branch pattern, and who can promote to it.",
  },
  // `org` is deliberately absent: RFC005 F5.6 built a real Billing section on
  // /organization-settings, so that route renders BillingPage rather than a
  // ComingSoon (see routes/_app/organization-settings.tsx). Removed when main
  // merged into billing-implementation — -backend-less-screens.test.tsx
  // asserts this map's key set equals its ROUTE_MODULES key set, so the two
  // must be dropped together.
  //
  // `build.tomerge`/`build.merged` are likewise absent (ex-uw7m58): ex-ari7fr
  // built the real backend, so both routes read from features/buildMerge/
  // instead of this map — same "drop together" rule the comment above
  // already established for `org`.
  //
  // `members` too (#1419): the Members tab reads the organization's people
  // from the edge (features/dashboard/members/OrgMembersPage.tsx). So does
  // `org.projects` (#1798), the organization's projects (OrgProjectsPage.tsx), and
  // `org.data` (#1834), the organization data export (OrgDataExportPage.tsx).
  "integrations.services": {
    title: "Integrations → Services",
    description: "Will show the services your product depends on, each with a status badge.",
  },
};

export const V3_SCREENS: Record<V3ScreenId, V3ScreenEntry> = {
  home: { section: "Home", path: "/home" },

  "prod.overview": { section: "Overview", path: "/product/overview" },
  // Memory (Kris, Oct 9, 2026, #3631): one page with three tabs, Decisions
  // (this path, the index tab), Documents and Forged's notes. Decisions had its own
  // screen and row (`prod.decisions`, issue #1402) and the notes and
  // documents were the Memories page (issue #149); /product/decisions and
  // /product/memories now redirect to their tabs. The id keeps its old name so
  // the sidebar row and its attention signal keep theirs. Not one of the
  // prototype's 18 literal screen ids, like `settings.project` below; the
  // route map test only requires the handoff's ids to appear here. Reads a
  // real backend, so not a BACKEND_LESS_SCREENS entry.
  "prod.memories": { section: "Memory", path: "/product/memory" },
  // The Forged's notes tab: what Forged writes for itself while it plans,
  // builds and chats (project_memories). The founder's word for one is "note".
  "memory.notes": { section: "Forged's notes", path: "/product/memory/notes" },
  // The Documents tab: the files the founder gave Forged (issue #1425).
  "memory.documents": { section: "Documents", path: "/product/memory/documents" },
  "prod.opportunities": { section: "Opportunities", path: "/product/opportunities" },
  // RFC022. Not one of the prototype's 18 literal screen ids: like `settings.project`
  // above it is an entry beyond the handoff set, which `-v3-route-map.test.tsx`
  // allows (it only requires the handoff's ids to appear here). Unlike the
  // three rows above, this one reads a real backend, so it is not in
  // BACKEND_LESS_SCREENS.
  "prod.designSystem": { section: "Design system", path: "/product/design-system" },
  // RFC023 Product Health. Real data, so not a BACKEND_LESS_SCREENS entry.
  //
  // It is NOT staff-only, whatever this comment used to say (Kris, Sep 22,
  // 2026: "anyone can access it"). Sidebar.tsx has no platform-admin gate on
  // this row and never has one in the code today. The claim was believed
  // while planning issue #512's L2 and had to be corrected by hand, so it is
  // written straight rather than deleted.
  "prod.health": { section: "Health", path: "/product/health" },

  // One note's page. No nav row of its own: Sidebar's activeNavFor inherits
  // the Memory sub-item for it, the way plan detail inherits Plans.
  memory: { section: "Note", path: "/product/memory/notes/$memoryId" },

  "plan.plans": { section: "Plans", path: "/plans" },
  // Tasks (Kris, Sep 18, 2026, "tasks next to plans"): one small fix or change
  // that becomes one pull request. Not one of the prototype's 18 literal
  // screen ids, like `settings.project` below; the route map test only
  // requires the handoff's ids to appear here.
  "plan.tasks": { section: "Tasks", path: "/tasks" },
  plan: { section: "Plan", path: "/plans/$planId" },
  // One task's page. No nav row of its own: Sidebar's activeNavFor inherits
  // the Tasks sub-item for it, the way plan detail inherits Plans.
  task: { section: "Task", path: "/tasks/$taskId" },
  // A plan's Layouts page (issue #695, Kris, Sep 18, 2026): the choices Forged
  // needs before its features build, at its own shareable link. No nav row of
  // its own: Sidebar's activeNavFor inherits the Plans sub-item for it.
  layouts: { section: "Layouts", path: "/artifacts/$publicId" },

  build: { section: "Building", path: "/build" },
  "build.tomerge": { section: "Ready to merge", path: "/build/tomerge" },
  "build.merged": { section: "Merged", path: "/build/merged" },

  // A feature's build page lives under Build only (Kris, Sep 28, 2026, issue
  // #1177 option A: Plan decides, Build builds), at "item.build", lighting
  // the Build list that shows it. The plan's and the task's addresses stay
  // as old addresses that forward there, replacing history, since emails,
  // pull request text and bookmarks link them: `item` at once (it names the
  // feature), `item.task` once it has looked up the task's one feature. This
  // reverses Sep 25's "one page, three doors" rule.
  //
  // A task's is titled "Task" (Kris, Sep 26, 2026): founders never hear a
  // task called a feature (Implement counts "3 features and 2 tasks",
  // workCount). The build page sets that title itself once it knows.
  item: { section: "Feature", path: "/plans/$planId/features/$featureId" },
  "item.build": { section: "Feature", path: "/build/features/$featureId" },
  "item.task": { section: "Task", path: "/tasks/$taskId/build" },

  "deploy.status": { section: "Status", path: "/deploy/status" },
  "deploy.releases": { section: "Releases", path: "/deploy/releases" },
  "deploy.env": { section: "Environments", path: "/deploy/environments" },

  // `/integrations` itself renders nothing: it redirects to the Repositories
  // sub-item (integrations/index.tsx), so every pre-existing link, bookmark
  // and test aimed at the pre-split screen still lands somewhere real. The
  // entry stays because `-v3-route-map.test.tsx` derives the handoff's own
  // screen-id list from the prototype source and requires each id to appear
  // here — `integrations` is one of those eighteen.
  integrations: { section: "Integrations", path: "/integrations" },
  "integrations.repos": { section: "Repositories", path: "/integrations/repositories" },
  // Repository detail (ex-qbfo90, RepositoryDetail.tsx) shows the row the
  // Repositories page already loads: its status, its review state, and Review
  // now or Review again. An active repository's summary card points to
  // Product > Overview; only a revoked or disconnected one shows the summary
  // itself, since Overview lists active ones only. No per-repository endpoint exists,
  // so it shows nothing that row does not carry. It has no nav row of its
  // own: Sidebar's activeNavFor inherits the Repositories sub-item for it,
  // the same way plan detail inherits Plans.
  "integrations.repo": { section: "Repository", path: "/integrations/repositories/$repoId" },
  "integrations.services": { section: "Services", path: "/integrations/services" },

  // Not one of the prototype's 18 literal screen ids (the handoff's own
  // `subs.settings` only lists members/usage) — this entry exists so the
  // real, already-shipped /settings route (owner report, 2026-08-14: created
  // but unreachable from the sidebar) gets a nav row. `-v3-route-map.test.tsx`
  // only requires the handoff's ids to appear here, not the reverse, so an
  // entry beyond that set is safe; its cold-load loop iterates every key in
  // this table regardless, so it still exercises this one.
  // The key matches the tab /settings shows, "Project" (G21.22, #2265). The
  // section said "General" while it was a sidebar row covering every Settings
  // tab; Settings is one sidebar row with no sub rows since Kris, Oct 10,
  // 2026, so the section names the tab too.
  "settings.project": { section: "Project", path: "/settings" },
  // A tab of Organization settings since #1419 (Kris, Sep 30, 2026); the
  // old /members address redirects here (routes/_app/members.tsx).
  members: { section: "Members", path: "/organization-settings/members" },
  // Organization settings' Projects tab (issue #1798): every project in the
  // organization, active and archived (Kris, Oct 10, 2026).
  "org.projects": { section: "Projects", path: "/organization-settings/projects" },
  // Organization settings' Data tab (issue #1834): an admin prepares one ZIP
  // of the organization's data and downloads it here.
  "org.data": { section: "Data", path: "/organization-settings/data" },
  // Settings' Usage tab since Kris, Oct 10, 2026, the second tab after
  // Project. The old /usage address redirects here (routes/_app/usage.tsx).
  usage: { section: "Usage", path: "/settings/usage" },

  // "Organization settings" (Kris, Sep 30, 2026, #1419). It was "Billing
  // settings" while the route rendered BillingPage and nothing else; it now
  // holds everything about the organization, billing first, with the
  // organization's members joining it as a tab. "settings" is carried
  // deliberately: the page is not only a record (Balance, History) but also
  // configuration -- Auto Reload, the billing profile and the saved card all
  // live here.
  //
  // The PATH is unchanged and cannot casually change: it is the default Stripe
  // checkout return URL (controlapi/config.go, with BILLING_CHECKOUT_SUCCESS_URL
  // unset in deploy) and is baked into delivered email templates (migration
  // 00210). The PLACEMENT is unchanged too -- owner decision, 2026-08-30.
  org: { section: "Organization settings", path: "/organization-settings" },
};

/** One page Ask Forged can link to. */
export interface LinkablePage {
  /** The page's name, as the sidebar or its tab names it. */
  name: string;
  /** What the page is for, in one line. The page card shows it. */
  purpose: string;
  /** The page's address. Never a `$param` path: a record is a citation. */
  route: string;
}

/**
 * The pages Ask Forged can link to (issues #3570 and #3572): the one list.
 * An answer's console link opens through the router only when its address is
 * here, and a link alone on its line shows as this page's card; any other
 * relative address stays inert. Ask Forged learns the list from one line of
 * product_facts.md, which productFactsLinkablePages.test.ts holds to this list
 * word for word, so the two cannot drift.
 *
 * Only pages that work: the coming soon pages (BACKEND_LESS_SCREENS) are left
 * out, and so is `/integrations`, which only redirects. The two Settings tabs
 * with no V3_SCREENS entry, AI models and Notifications, are written out.
 */
export const LINKABLE_PAGES: readonly LinkablePage[] = [
  { name: "Home", purpose: "What needs you, what is being built, and your conversation.", route: V3_SCREENS.home.path },
  { name: "Plans", purpose: "Every plan, from its first questions to its features.", route: V3_SCREENS["plan.plans"].path },
  { name: "Tasks", purpose: "Small changes, each one pull request.", route: V3_SCREENS["plan.tasks"].path },
  { name: "Building", purpose: "Work being built right now.", route: V3_SCREENS.build.path },
  { name: "Ready to merge", purpose: "Work whose pull request is open and waiting.", route: V3_SCREENS["build.tomerge"].path },
  { name: "Merged", purpose: "Everything that has merged, newest first.", route: V3_SCREENS["build.merged"].path },
  { name: "Overview", purpose: "What Forged knows about your product from its code.", route: V3_SCREENS["prod.overview"].path },
  { name: "Memory", purpose: "What has been decided for your product.", route: V3_SCREENS["prod.memories"].path },
  { name: "Documents", purpose: "Files you give Forged to read, such as specs.", route: V3_SCREENS["memory.documents"].path },
  { name: "Forged's notes", purpose: "Short facts Forged keeps about your code.", route: V3_SCREENS["memory.notes"].path },
  { name: "Design system", purpose: "Your colors, text sizes, components and brand files.", route: V3_SCREENS["prod.designSystem"].path },
  { name: "Health", purpose: "A check of a repository's code, scored in six areas.", route: V3_SCREENS["prod.health"].path },
  { name: "Repositories", purpose: "Connect and manage the repositories Forged builds in.", route: V3_SCREENS["integrations.repos"].path },
  { name: "Project settings", purpose: "How much builds at once, and archiving the project.", route: V3_SCREENS["settings.project"].path },
  { name: "Usage", purpose: "What this project has used, and your balance.", route: V3_SCREENS.usage.path },
  { name: "AI models", purpose: "The model and effort for chat, planning and building.", route: "/settings/ai-models" },
  { name: "Notifications", purpose: "Which emails Forged sends you.", route: "/settings/notifications" },
  { name: "Organization settings", purpose: "Billing, members, projects and data.", route: V3_SCREENS.org.path },
  { name: "Members", purpose: "Who is in the organization, their roles and invitations.", route: V3_SCREENS.members.path },
  { name: "Projects", purpose: "Every project in the organization.", route: V3_SCREENS["org.projects"].path },
  { name: "Data", purpose: "Download a copy of the organization's data.", route: V3_SCREENS["org.data"].path },
];

/** The listed page whose address is exactly `href`, or undefined. Exact on
 *  purpose: no query, no fragment and no trailing slash, so nothing the model
 *  writes can reach past the list. */
export function linkablePageFor(href: string | undefined): LinkablePage | undefined {
  if (href === undefined) return undefined;
  const address = href.trim();
  return LINKABLE_PAGES.find((page) => page.route === address);
}

/**
 * Pages the console draws with no navigation (Kris, Sep 25, 2026): the
 * Forged logo and the page. The Layouts page opens in a tab of its own from
 * "Open full size", and a sidebar, top bar title, History and profile beside
 * it read as a place to navigate from rather than one set of choices to make.
 * Its top bar keeps the Ask Forged button, in the same spot as every other
 * page (Kris, Oct 7, 2026, click path row 23).
 */
export function isBarePath(pathname: string): boolean {
  return pathname.startsWith("/artifacts/");
}

/** The build page's `?from=`: the Build list that opened it, when it was not
 *  Implement, so the sidebar lights that list and Back returns to it from the
 *  first moment. Only Build's own lists: the plan's and the task's old
 *  addresses forward to the build page, which lights the list that shows the
 *  feature, never Plans or Tasks for where it was opened from (Kris, Sep 28,
 *  2026, issue #1177; #1260 item 1). A feature off Build lights its plan or
 *  task, worked out from the feature itself (buildDoorPage), never from the
 *  address. */
export type BuildFeatureFrom = "tomerge" | "merged";

/** Every `?from=` value the build page takes, and nothing else: the route's
 *  validator and the sidebar both read the address through it. */
export const BUILD_FEATURE_FROM_VALUES = ["tomerge", "merged"] as const satisfies readonly BuildFeatureFrom[];

/** Whether an address's `from` is one the build page takes. */
export function isBuildFeatureFrom(value: unknown): value is BuildFeatureFrom {
  return (BUILD_FEATURE_FROM_VALUES as readonly unknown[]).includes(value);
}
